import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Textarea,
  Button,
  Flex,
  Avatar,
  Heading,
  IconButton,
  Spinner,
  Tooltip,
  useDisclosure,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  ArrowBackIcon, 
  DownloadIcon, 
  ChevronDownIcon, 
  InfoIcon
} from '@chakra-ui/icons';
import { useAppToast } from '../utils/toast';
import { formatMessageHtml } from '../utils/formatMessage';
import { sessionService, chatService } from '../services/api';

function Chat() {
  const { sessionId } = useParams();
  const { showErrorToast } = useAppToast();
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  // Colors for styling
  const userBubbleColor = useColorModeValue('brand.500', 'brand.400');
  const characterBubbleColor = useColorModeValue('gray.200', 'gray.600');
  const userTextColor = useColorModeValue('white', 'white');
  const characterTextColor = useColorModeValue('black', 'white');
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    // Refetch only when the session changes (mount / navigation). Deliberately NOT
    // dependent on the auth token: a mid-send token refresh (api.js dispatches
    // 'auth:token-refreshed' -> App setToken) would otherwise re-run this and
    // overwrite the optimistic post with a pre-save DB snapshot, hiding the
    // just-sent message until the next load. The axios instance already adopts the
    // refreshed token (App.js), so no refetch is needed on token change.
    fetchSession();
  }, [sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [session?.messages]);

  const fetchSession = async () => {
    try {
      setIsLoading(true);
      const data = await sessionService.getSessionById(sessionId);
      setSession(data);
    } catch (error) {
      console.error('Error fetching session:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;

    const userMessage = message.trim();
    
    // Add user message immediately and clear input
    setSession(prev => ({
      ...prev,
      messages: [
        ...prev.messages,
        { role: 'user', content: userMessage, timestamp: new Date() }
      ]
    }));
    
    setMessage('');
    setIsSending(true);
    
    try {
      const data = await chatService.sendMessage(sessionId, userMessage);

      // Add character response
      setSession(prev => ({
        ...prev,
        messages: [
          ...prev.messages,
          { role: 'character', content: data.response, timestamp: new Date() }
        ]
      }));
    } catch (error) {
      console.error('Error sending message:', error);
      const detail = error.response?.data?.error
        || error.message
        || 'Your message could not be sent. Please try again in a moment.';
      showErrorToast('Message not sent', detail);
      // Roll back the optimistic user bubble and restore the text so it can be edited/retried.
      setSession(prev => ({ ...prev, messages: prev.messages.slice(0, -1) }));
      setMessage(userMessage);
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const exportToTxt = () => {
    if (!session) return;
    
    const characterName = session.characterId.character_profile?.character_name || 'Character';
    let content = `Chat Session: ${session.name}\nCharacter: ${characterName}\n\n`;
    
    session.messages.forEach(msg => {
      const label = msg.role === 'user' ? 'Player:' : `${characterName}:`;
      content += `${label}\n${msg.content}\n\n`;
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${session.name.replace(/[^a-z0-9]/gi, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="70vh">
        <Spinner size="xl" color="brand.500" />
      </Flex>
    );
  }

  if (!session) {
    return (
      <Box textAlign="center" py={10}>
        <Heading size="lg">Session not found</Heading>
        <Button mt={4} onClick={() => navigate('/')} colorScheme="brand">
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const characterName = session.characterId.character_profile?.character_name || 'Character';

  return (
    <Box bg={bgColor} minH="calc(100vh - 180px)" pb={8}>
      <Container maxW="container.xl" pt={4}>
        <Flex justify="space-between" align="center" mb={4}>
          <Button
            as={RouterLink}
            to="/"
            leftIcon={<ArrowBackIcon />}
            colorScheme="brand"
            variant="outline"
          >
            Back to Dashboard
          </Button>
          
          <Heading size="md" noOfLines={1} textAlign="center" flex="1" mx={4}>
            {session.name} - {characterName}
          </Heading>
          
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} colorScheme="brand">
              Actions
            </MenuButton>
            <MenuList>
              <MenuItem 
                icon={<InfoIcon />}
                onClick={() => navigate(`/view-character/${session.characterId._id}`)}
              >
                View Character Profile
              </MenuItem>
              <MenuItem icon={<DownloadIcon />} onClick={exportToTxt}>
                Export to TXT
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        
        <Box
          bg={useColorModeValue('white', 'gray.700')}
          borderRadius="lg"
          boxShadow="md"
          p={4}
          mb={4}
          height="calc(100vh - 280px)"
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'var(--chakra-colors-gray-300)',
              ...useColorModeValue({}, { background: 'var(--chakra-colors-gray-600)' }),
              borderRadius: '24px',
            },
          }}
        >
          <VStack spacing={4} align="stretch">
            {session.messages.map((msg, index) => (
              <Flex
                key={index}
                justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
              >
                <Box
                  maxW={{ base: '85%', md: '70%' }}
                  p={3}
                  borderRadius="lg"
                  bg={msg.role === 'user' ? userBubbleColor : characterBubbleColor}
                  color={msg.role === 'user' ? userTextColor : characterTextColor}
                  position="relative"
                >
                  <Flex align="center" mb={1}>
                    {msg.role !== 'user' && (
                      <Avatar
                        size="xs"
                        name={characterName}
                        src={session.characterId.imageData ? `/api/characters/${session.characterId._id}/image` : null}
                        mr={2}
                      />
                    )}
                    <Text fontWeight="bold" fontSize="sm">
                      {msg.role === 'user' ? 'You' : characterName}
                    </Text>
                    {msg.timestamp && (
                      <Text fontSize="xs" color={msg.role === 'user' ? 'white' : 'gray.500'} ml="auto">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </Text>
                    )}
                  </Flex>
                  
                  <Text
                    whiteSpace="pre-wrap"
                    dangerouslySetInnerHTML={{ __html: formatMessageHtml(msg.content) }}
                  />
                </Box>
              </Flex>
            ))}
            
            {isSending && (
              <Flex justify="flex-start">
                <Box
                  maxW={{ base: '85%', md: '70%' }}
                  p={3}
                  borderRadius="lg"
                  bg={characterBubbleColor}
                  color={characterTextColor}
                >
                  <Flex align="center" mb={1}>
                    <Avatar
                      size="xs"
                      name={characterName}
                      src={session.characterId.imageData ? `/api/characters/${session.characterId._id}/image` : null}
                      mr={2}
                    />
                    <Text fontWeight="bold" fontSize="sm">{characterName}</Text>
                  </Flex>
                  <Text><em>Typing...</em></Text>
                </Box>
              </Flex>
            )}
            
            <div ref={messagesEndRef} />
          </VStack>
        </Box>
        
        <Box bg={useColorModeValue('white', 'gray.700')} borderRadius="lg" boxShadow="md" p={4}>
          <form onSubmit={sendMessage}>
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              size="md"
              resize="none"
              rows={3}
              mb={3}
              isDisabled={isSending}
              onKeyDown={handleKeyDown}
              _focus={{ 
                borderColor: 'brand.500', 
                boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' 
              }}
            />
            <Flex justify="space-between" align="center">
              <Text fontSize="xs" color="gray.500">
                Press Enter to send, Shift+Enter for new line
              </Text>
              <Button
                type="submit"
                colorScheme="brand"
                isLoading={isSending}
                loadingText="Sending"
                isDisabled={!message.trim()}
              >
                Send
              </Button>
            </Flex>
          </form>
        </Box>
      </Container>
    </Box>
  );
}

export default Chat;